// Allowlist HTML sanitizer for CMS rich text. Pure regex tokenizer (no DOM), so it
// runs identically in the API Worker (write path), the admin SPA (editor output)
// and the Next.js site (render path).
//
// Adapted from medverse's jobs rich-text sanitizer and widened for editorial
// content: headings (with anchor ids), quotes, rules, links, images (optionally in a
// figure with a caption) and tables.
// Attributes are rebuilt from a per-tag allowlist, never copied through, so style=,
// on*= and javascript: URLs cannot survive.

import { escapeHtml } from './escape-html'

export const RICH_TEXT_ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'em',
  'u',
  's',
  'ol',
  'ul',
  'li',
  'h2',
  'h3',
  'h4',
  'blockquote',
  'hr',
  'a',
  'img',
  'figure',
  'figcaption',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
] as const
export type RichTextAllowedTag = (typeof RICH_TEXT_ALLOWED_TAGS)[number]

const ALLOWED_TAGS = new Set<string>(RICH_TEXT_ALLOWED_TAGS)
const VOID_TAGS = new Set(['br', 'hr', 'img'])
// Unknown containers are unwrapped (text kept); these lose their content too.
const DROP_WITH_CONTENT = new Set(['script', 'style', 'iframe', 'object', 'template', 'noscript'])

export const RICH_TEXT_MAX_VISIBLE_CHARS = 60_000
export const RICH_TEXT_MAX_RAW_CHARS = 250_000

// Comment/CDATA, a start or end tag (name + attribute tail), a text run, or a lone `<`.
const TOKEN_RE = /<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<\/?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*)>|[^<]+|</g
const ATTR_RE = /([a-zA-Z][a-zA-Z0-9-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/g

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
}

function decodeEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, body: string) => {
    if (body[0] === '#') {
      const cp = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10)
      return Number.isFinite(cp) && cp <= 0x10ffff ? String.fromCodePoint(cp) : match
    }
    return NAMED_ENTITIES[body] ?? match
  })
}

/** Relative paths, http(s), and optionally mailto/tel. Rejects javascript:, data:, vbscript:, protocol-relative. */
export function isSafeUrl(raw: string, { allowMailto = true }: { allowMailto?: boolean } = {}): boolean {
  // Browsers ignore whitespace and control characters inside a scheme, so strip them before checking.
  // eslint-disable-next-line no-control-regex -- deliberately matching control characters
  const url = decodeEntities(raw).replace(/[\x00-\x20\x7f]+/g, '')
  if (url === '' || url.startsWith('//')) return false
  if (url.startsWith('/') || url.startsWith('#') || url.startsWith('?')) return true
  const scheme = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(url)?.[1]?.toLowerCase()
  if (!scheme) return !url.includes(':')
  return scheme === 'https' || scheme === 'http' || (allowMailto && (scheme === 'mailto' || scheme === 'tel'))
}

function parseAttrs(raw: string): Map<string, string> {
  const attrs = new Map<string, string>()
  for (const m of raw.matchAll(ATTR_RE)) {
    const name = m[1]?.toLowerCase()
    if (name && !attrs.has(name)) attrs.set(name, decodeEntities(m[2] ?? m[3] ?? m[4] ?? ''))
  }
  return attrs
}

const ANCHOR_ID_RE = /^[a-z0-9][a-z0-9_-]{0,80}$/
const HEADINGS = new Set<RichTextAllowedTag>(['h2', 'h3', 'h4'])

/** Attribute string rebuilt from scratch, or null when the element must be dropped. */
function safeAttrs(tag: RichTextAllowedTag, raw: string): string | null {
  if (HEADINGS.has(tag)) {
    // In-page anchors for table-of-contents links (`#section`). Lowercase slug ids only.
    const id = parseAttrs(raw).get('id')?.trim()
    return id && ANCHOR_ID_RE.test(id) ? ` id="${id}"` : ''
  }
  if (tag === 'th' || tag === 'td') {
    const span = parseAttrs(raw)
    let out = ''
    for (const name of ['colspan', 'rowspan']) {
      const v = span.get(name)
      if (v && /^[1-9]\d?$/.test(v)) out += ` ${name}="${v}"`
    }
    return out
  }
  if (tag !== 'a' && tag !== 'img') return ''
  const attrs = parseAttrs(raw)

  if (tag === 'a') {
    const href = attrs.get('href')?.trim()
    // Keep a bare <a> so its closing tag stays balanced; it just isn't a link.
    if (!href || !isSafeUrl(href)) return ''
    const external = /^https?:/i.test(href)
    return ` href="${escapeHtml(href)}"${external ? ' target="_blank" rel="noopener noreferrer"' : ''}`
  }

  const src = attrs.get('src')?.trim()
  if (!src || !isSafeUrl(src, { allowMailto: false })) return null
  const alt = attrs.get('alt') ?? ''
  let dims = ''
  for (const d of ['width', 'height']) {
    const v = attrs.get(d)
    if (v && /^\d{1,5}$/.test(v)) dims += ` ${d}="${v}"`
  }
  return ` src="${escapeHtml(src)}" alt="${escapeHtml(alt)}"${dims} loading="lazy" decoding="async"`
}

interface WalkOptions {
  onOpen?: (tag: RichTextAllowedTag, rawAttrs: string) => void
  onClose?: (tag: RichTextAllowedTag) => void
  onText?: (text: string) => void
}

// One tokenizer walk shared by the sanitizer and the text extractors, so "what is
// allowed" has a single implementation.
function walk(html: string, options: WalkOptions): void {
  const dropStack: string[] = []

  for (const match of html.matchAll(TOKEN_RE)) {
    const token = match[0]
    const isTag = token.length > 1 && token[0] === '<' && token[1] !== '!'

    if (!isTag) {
      if (token.startsWith('<!')) continue
      if (dropStack.length === 0) options.onText?.(decodeEntities(token))
      continue
    }

    const isClosing = token[1] === '/'
    const tagName = (match[1] ?? '').toLowerCase()
    if (!tagName) continue

    if (dropStack.length > 0) {
      if (tagName === dropStack[dropStack.length - 1]) {
        if (isClosing) dropStack.pop()
        else dropStack.push(tagName)
      }
      continue
    }

    if (DROP_WITH_CONTENT.has(tagName)) {
      if (!isClosing) dropStack.push(tagName)
      continue
    }

    if (!ALLOWED_TAGS.has(tagName)) continue

    const tag = tagName as RichTextAllowedTag
    if (VOID_TAGS.has(tag)) {
      if (!isClosing) options.onOpen?.(tag, match[2] ?? '')
      continue
    }
    if (isClosing) options.onClose?.(tag)
    else options.onOpen?.(tag, match[2] ?? '')
  }
}

const HTML_TAG_RE = new RegExp(`<(?:${RICH_TEXT_ALLOWED_TAGS.join('|')})\\b`, 'i')

/** True once a value contains no allowed tag at all — i.e. it predates the editor. */
export function looksLikeLegacyPlainText(value: string): boolean {
  return !HTML_TAG_RE.test(value)
}

/** Idempotent allowlist sanitize. Run on write (API) and again on render. */
export function sanitizeRichTextHtml(html: string): string {
  const bounded = html.length > RICH_TEXT_MAX_RAW_CHARS ? html.slice(0, RICH_TEXT_MAX_RAW_CHARS) : html
  let out = ''
  walk(bounded, {
    onOpen: (tag, raw) => {
      const attrs = safeAttrs(tag, raw)
      if (attrs !== null) out += `<${tag}${attrs}>`
    },
    onClose: (tag) => {
      out += `</${tag}>`
    },
    onText: (text) => {
      out += escapeHtml(text)
    },
  })
  return out.length > RICH_TEXT_MAX_RAW_CHARS ? out.slice(0, RICH_TEXT_MAX_RAW_CHARS) : out
}

/** Rendered characters, ignoring markup — what length caps are measured against. */
export function getVisibleTextLength(html: string): number {
  let length = 0
  walk(html, { onText: (text) => (length += text.length) })
  return length
}

/** Plain text with tags stripped and whitespace collapsed — for meta descriptions and excerpts. */
export function stripToPlainText(html: string): string {
  let out = ''
  walk(html, {
    onText: (text) => (out += text),
    onClose: () => (out += ' '),
  })
  return out.replace(/\s+/g, ' ').trim()
}

export function plainTextToRichTextHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .filter((p) => p.trim().length > 0)
    .map((p) => `<p>${escapeHtml(p.trim()).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

/** The single entry point every renderer calls before dangerouslySetInnerHTML. */
export function toSafeRichTextHtml(value: string): string {
  return sanitizeRichTextHtml(looksLikeLegacyPlainText(value) ? plainTextToRichTextHtml(value) : value)
}
